import { toDeliveryAddressParams } from '@iheartjane/cart/utils';
import type { CheckoutProps } from '@iheartjane/data-access';

import type {
  CheckoutNewPaymentMethods,
  CheckoutNewRequest,
} from '@jane/shared-ecomm/types';

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

/** The form payload plus the fields `checkout.tsx` adds. */
export type CheckoutPayload = DistributiveOmit<
  CheckoutNewRequest,
  'customer'
> & {
  aeropay_account_verified: boolean;
  customer: Omit<CheckoutNewRequest['customer'], 'first_name' | 'last_name'> & {
    name: string;
  };
};

type KeyOfAny<T> = T extends unknown ? keyof T : never;
type ValueOfAny<T, Key extends PropertyKey> =
  T extends Record<Key, infer Value> ? Value : never;

/** Every method's fields; a payload carries only the selected method's. */
type PaymentFields = {
  [Key in KeyOfAny<CheckoutNewPaymentMethods>]?: ValueOfAny<
    CheckoutNewPaymentMethods,
    Key
  >;
};

export const toV3CheckoutProps = (
  payload: CheckoutPayload,
  { appMode, jwt }: { appMode: string; jwt?: string | null }
): CheckoutProps => {
  const payment: PaymentFields = payload;

  return {
    aeropayAccountVerified: payload.aeropay_account_verified,
    aeropayBankAccountId: payment.aeropay_bank_account_id,
    ageVerification: payload.age_verification,
    anonymousUserId: payload.anonymous_user_id,
    appMode,
    canpayToken: payment.canpay_token,
    canpayV2remotepayTransaction: payment.canpay_v2remotepay_transaction,
    cartUuid: payload.uuid,
    customer: payload.customer,
    customerPickupAddress: toDeliveryAddressParams(
      payload.customer_pickup_address
    ),
    deliveryAddress: toDeliveryAddressParams(payload.delivery_address),
    // An unpicked slot is NaN, which v1 posts as `null` and `checkout` drops.
    deliveryFinishedAt: payload.delivery_finished_at,
    deliveryStartedAt: payload.delivery_started_at,
    deviceId: payload.j_device_id,
    headlessCheckoutPartnerId: payload.headless_checkout_partner_id,
    isCurbsidePickup: payload.is_curbside_pickup,
    isMobile: payload.is_mobile,
    jwt,
    medicalIdentification: payload.medical_identification,
    message: payload.message,
    monerisTicket: payment.moneris_ticket ?? undefined,
    payfirmaCardCvv2: payment.payfirma_card_cvv2,
    payfirmaCardExpiryMonth: payment.payfirma_card_expiry_month,
    payfirmaCardExpiryYear: payment.payfirma_card_expiry_year,
    payfirmaCardNumber: payment.payfirma_card_number,
    payfirmaCardPostalCode: payment.payfirma_card_postal_code,
    paymentMethod: payload.payment_method,
    promotionsOptOut: payload.promotions_opt_out,
    recaptchaToken: payload.recaptcha_token ?? undefined,
    reservationMode: payload.reservation_mode,
    squarePaymentToken: payment.square_payment_token,
    strongholdPaymentSourceId: payment.stronghold_payment_source_id,
    tags: payload.tags,
    tipAmount: payload.tip_amount,
    userConsent: payload.user_consent,
    // `ProcessCheckout` reads only the param, never the cart's selection.
    userGroupSpecialId:
      payload.user_group_special_id == null
        ? undefined
        : Number(payload.user_group_special_id),
  };
};
